// Overview → Tracking card. The aggregate view of every carrier-tracking
// record across the project's POs, folded into the project Overview per CEO
// 2026-07-29 ("tracking … should be in the overview section … its own tracking
// section is redundant"). Read-only; tracking ENTRY still happens on each PO.
//
// Adapted from the retired ProjectShippingTab (route `/projects/:id/shipping`
// now redirects to Overview). Same `/projects/:id/shipments` data source — no
// new endpoint — and the SAME columns (From / Status / Carrier·Tracking / Qty /
// Ship by / Updated), so the per-shipment ship-by date on split shipments stays
// reachable here (it lives nowhere else in the app). House table-in-a-card
// treatment (cream thead + hover rows + symmetric cell padding).
//
// Omit-when-empty is the CALLER's job (Overview only mounts this once the
// project has at least one PO — no empty shell before production exists). Once
// mounted, the "no tracking yet" state earns its space: POs exist, tracking is
// simply pending suppliers shipping.
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { ExternalLink } from "lucide-react";
import { api } from "@/lib/api";
import { StatusPill } from "@/components/ui/StatusPill";
import { timeAgo } from "@/lib/utils";

interface ShipmentRow {
  id: string;
  purchaseOrderId: string;
  poNumber: number;
  legType: string | null;
  originVendorId: string | null;
  originVendorName: string | null;
  status: string;
  carrier: string | null;
  trackingNumber: string | null;
  trackingUrl: string | null;
  shippedQuantity: number | null;
  projectedShipDate: string | null;
  actualShipDate: string | null;
  requiredShipDate: string | null;
  shippedAt: string | null;
  deliveredAt: string | null;
  createdAt: string | null;
}

interface ShipmentsListResponse {
  rows: ShipmentRow[];
}

function statusTone(
  s: string,
): "active" | "prospect" | "dormant" | "closed" | "warn" | "info" {
  switch (s) {
    case "pending":
      return "dormant";
    case "in_transit":
    case "out_for_delivery":
      return "prospect";
    case "delivered":
      return "active";
    case "exception":
      return "warn";
    case "returned":
      return "closed";
    default:
      return "info";
  }
}

function formatDate(s: string | null): string {
  if (!s) return "—";
  if (!/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
  return new Date(s + "T00:00:00").toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
  });
}

export function ProjectTrackingCard({ projectId }: { projectId: string }) {
  const { data: list, isLoading } = useQuery({
    queryKey: ["project", projectId, "shipments"],
    queryFn: () =>
      api<ShipmentsListResponse>(`/projects/${projectId}/shipments`),
  });

  const rows = list?.rows ?? [];

  return (
    <section className="mb-6 rounded-lg border border-brown-100 bg-white p-5">
      <div className="mb-3 flex items-center justify-between">
        <h2 className="text-[11px] font-medium uppercase tracking-[0.12em] text-muted">
          Tracking
        </h2>
        <p className="text-xs text-brown-300">
          {rows.length}{" "}
          {rows.length === 1 ? "shipment tracked" : "shipments tracked"}
        </p>
      </div>

      {isLoading ? (
        <p className="text-sm text-brown-300">Loading tracking…</p>
      ) : rows.length === 0 ? (
        <p className="text-sm text-brown-300">
          No tracking yet. Add tracking on each PO once suppliers ship — split
          shipments are supported.
        </p>
      ) : (
        <div className="overflow-hidden rounded-lg border border-brown-50">
          <table className="w-full text-sm">
            <thead className="border-b border-[#e7e7e4] bg-[#fafaf8] text-left text-[11px] font-medium text-muted">
              <tr>
                <th className="px-4 py-2.5">From</th>
                <th className="px-4 py-2.5">Status</th>
                <th className="px-4 py-2.5">Carrier / Tracking</th>
                <th className="px-4 py-2.5">Qty</th>
                <th className="px-4 py-2.5">Ship by</th>
                <th className="px-4 py-2.5">Updated</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-brown-50">
              {rows.map((s) => (
                <tr key={s.id} className="hover:bg-cream-50">
                  <td className="px-4 py-2.5 align-top">
                    <Link
                      to={`/projects/${projectId}/pos/${s.purchaseOrderId}`}
                      className="font-semibold text-brown-500 underline-offset-4 hover:underline"
                    >
                      PO #{s.poNumber}
                    </Link>
                    <div className="mt-0.5 text-xs text-brown-300">
                      {s.originVendorName ?? "—"}
                      {s.legType && ` · ${s.legType.replace(/_/g, " ")}`}
                    </div>
                  </td>
                  <td className="px-4 py-2.5 align-top">
                    <StatusPill tone={statusTone(s.status)}>
                      {s.status.replace(/_/g, " ")}
                    </StatusPill>
                  </td>
                  <td className="px-4 py-2.5 align-top text-brown-400">
                    {s.carrier ? (
                      <div>
                        <div className="font-semibold uppercase tracking-wide">
                          {s.carrier}
                        </div>
                        {s.trackingNumber && (
                          <div className="mt-0.5 inline-flex items-center gap-1 text-xs text-brown-300">
                            {s.trackingUrl ? (
                              <a
                                href={s.trackingUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center gap-1 hover:text-brown-500 hover:underline"
                              >
                                {s.trackingNumber}
                                <ExternalLink className="h-3 w-3" />
                              </a>
                            ) : (
                              s.trackingNumber
                            )}
                          </div>
                        )}
                      </div>
                    ) : (
                      <span className="text-brown-300">—</span>
                    )}
                  </td>
                  <td className="px-4 py-2.5 align-top text-brown-400">
                    {s.shippedQuantity ?? "—"}
                  </td>
                  <td className="px-4 py-2.5 align-top text-brown-400">
                    {formatDate(
                      s.actualShipDate ??
                        s.projectedShipDate ??
                        s.requiredShipDate,
                    )}
                  </td>
                  <td className="px-4 py-2.5 align-top text-brown-400">
                    {timeAgo(s.deliveredAt ?? s.shippedAt ?? s.createdAt)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
